/**
 * PageIntro — reusable "what is this page for" header block.
 *
 * Rendered at the top of every Ultra Admin page so operators can immediately
 * understand what the page does, what data lives here, and what actions are
 * safe. Bilingual (TR/EN). Optional tips list + severity hint for dangerous
 * pages.
 *
 * Props
 * ─────
 *   title      Page title (plain string — pass already-localised text)
 *   description Short one-to-three sentence explanation
 *   tips       Optional array of short bullet points (tactical hints)
 *   tone       'info' (default) | 'warning' | 'danger' — tints accent color
 *   icon       Optional Lucide icon component (default: Info)
 *
 * Usage (inside any Ultra Admin page):
 *   const tr = language === 'tr';
 *   <PageIntro
 *     title={tr ? 'Kullanıcı Yönetimi' : 'User Management'}
 *     description={tr ? '...' : '...'}
 *     tips={[
 *       tr ? '...' : '...',
 *       tr ? '...' : '...',
 *     ]}
 *   />
 */
import React, { useState } from 'react';
import { Info, AlertTriangle, ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';

const TONE = {
  info: {
    bg: 'bg-indigo-50/60',
    border: 'border-indigo-200',
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-700',
    titleColor: 'text-indigo-900',
    bodyColor: 'text-indigo-800',
    tipBg: 'bg-white',
    tipBullet: 'text-indigo-500',
    defaultIcon: Info,
  },
  warning: {
    bg: 'bg-amber-50/60',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-700',
    titleColor: 'text-amber-900',
    bodyColor: 'text-amber-800',
    tipBg: 'bg-white',
    tipBullet: 'text-amber-600',
    defaultIcon: AlertTriangle,
  },
  danger: {
    bg: 'bg-red-50/60',
    border: 'border-red-200',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-700',
    titleColor: 'text-red-900',
    bodyColor: 'text-red-800',
    tipBg: 'bg-white',
    tipBullet: 'text-red-600',
    defaultIcon: ShieldAlert,
  },
};

export default function PageIntro({
  title,
  description,
  tips,
  tone = 'info',
  icon,
  collapsible = true,
  defaultCollapsed = false,
}) {
  const palette = TONE[tone] || TONE.info;
  const Icon = icon || palette.defaultIcon;
  const [collapsed, setCollapsed] = useState(!!defaultCollapsed);
  const hasTips = Array.isArray(tips) && tips.length > 0;

  return (
    <div
      className={`mb-6 rounded-2xl border ${palette.border} ${palette.bg} p-4 sm:p-5`}
      data-testid="page-intro"
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 h-9 w-9 rounded-xl ${palette.iconBg} flex items-center justify-center`}>
          <Icon className={palette.iconColor} style={{ height: 18, width: 18 }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h2 className={`font-bold text-base sm:text-lg ${palette.titleColor} leading-tight`}>
              {title}
            </h2>
            {collapsible && hasTips && (
              <button
                type="button"
                onClick={() => setCollapsed(c => !c)}
                className={`shrink-0 inline-flex items-center gap-1 text-xs font-semibold ${palette.iconColor} hover:opacity-70 transition-opacity px-2 py-1 rounded-md`}
                data-testid="page-intro-toggle"
                aria-expanded={!collapsed}
              >
                {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
              </button>
            )}
          </div>
          {description && (
            <p className={`mt-1 text-sm ${palette.bodyColor} leading-relaxed`}>
              {description}
            </p>
          )}
          {!collapsed && hasTips && (
            <ul className="mt-3 space-y-1.5" data-testid="page-intro-tips">
              {tips.map((tip, i) => (
                <li
                  key={i}
                  className={`flex items-start gap-2 text-xs ${palette.bodyColor} ${palette.tipBg} rounded-lg px-2.5 py-1.5`}
                >
                  <span className={`shrink-0 mt-1 h-1 w-1 rounded-full ${palette.tipBullet.replace('text-', 'bg-')}`} />
                  <span className="leading-snug">{tip}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
